import { motion } from "framer-motion";
import { Reveal, SectionTag, EASE, MaskedLines } from "@/components/Reveal";

const AREAS = [
  {
    num: "01",
    label: "Service Businesses",
    copy: "Customer-facing businesses designed around practical, recurring service needs.",
  },
  {
    num: "02",
    label: "Digital Service Platforms",
    copy: "Platforms that connect customers, workflows, service delivery and business operations.",
  },
  {
    num: "03",
    label: "Business Systems",
    copy: "CRM, automation and internal technology that help teams operate with greater structure.",
  },
];

export default function WhatWeDo() {
  return (
    <section
      id="what-we-do"
      data-testid="what-we-do-section"
      className="scroll-mt-20 border-t border-white/5 bg-[#090807] py-20 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionTag index="02">What We Build</SectionTag>
            </Reveal>
            <h2
              data-testid="what-we-do-headline"
              className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
            >
              <MaskedLines delay={0.1}>
                Businesses supported
                <br />
                by technology,
                <br />
                <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
                  systems and operations.
                </span>
              </MaskedLines>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xl self-end text-base font-normal leading-relaxed text-slate-300 sm:text-lg lg:ml-auto">
              We focus on business models where better digital access,
              structured operations and connected technology can improve the
              customer experience and strengthen execution.
            </p>
          </Reveal>
        </div>

        {/* three build areas converging into customer value */}
        <div data-testid="what-we-build" className="relative mt-16 lg:pr-64">
          {/* convergence spine (desktop) */}
          <div className="pointer-events-none absolute bottom-10 right-0 top-10 hidden w-64 lg:block" aria-hidden="true">
            <motion.span
              className="absolute right-24 top-0 h-full w-px origin-top bg-gradient-to-b from-[#D4AF37]/50 via-[#D4AF37]/30 to-[#D4AF37]/50"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.8, ease: EASE, delay: 0.4 }}
            />
            <motion.span
              className="absolute right-[calc(6rem-5px)] top-1/2 h-[11px] w-[11px] -translate-y-1/2 rotate-45 bg-[#D4AF37]"
              initial={{ opacity: 0, scale: 0.3 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE, delay: 1.6 }}
            />
            <motion.span
              className="absolute right-[calc(6rem-5px)] top-1/2 h-[22px] w-[22px] -translate-y-1/2 rotate-45 border border-[#D4AF37]/35"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: [0.2, 0.6, 0.2] }}
              viewport={{ once: true }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
            />
            <motion.p
              className="absolute right-0 top-1/2 origin-right -translate-y-1/2 text-[10px] font-medium uppercase tracking-[0.3em] text-[#E7CE8B]"
              initial={{ opacity: 0, x: 10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.9 }}
            >
              Customer
              <br />
              Value
            </motion.p>
          </div>

          <div className="border-t border-white/10">
            {AREAS.map((a, i) => (
              <Reveal key={a.num} delay={0.12 * i}>
                <div
                  data-testid={`what-we-build-item-${i + 1}`}
                  className="group relative grid items-center gap-3 border-b border-white/10 py-9 transition-colors duration-500 hover:border-[#D4AF37]/40 sm:grid-cols-[auto_1fr] sm:gap-10"
                >
                  {/* connector tick into the spine (desktop) */}
                  <motion.span
                    className="absolute -right-64 top-1/2 hidden h-px w-40 origin-left bg-gradient-to-r from-[#D4AF37]/50 to-[#D4AF37]/20 lg:block"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.8 + i * 0.25 }}
                    aria-hidden="true"
                  />
                  <span className="font-serif text-4xl font-normal leading-none tracking-tight text-white/[0.07] transition-colors duration-700 group-hover:text-[#D4AF37]/25" aria-hidden="true">
                    {a.num}
                  </span>
                  <span>
                    <span className="block font-serif text-xl font-normal uppercase tracking-tight text-slate-100 transition-colors duration-500 group-hover:text-white sm:text-2xl">
                      {a.label}
                    </span>
                    <span className="mt-2.5 block max-w-lg text-[15px] font-normal leading-relaxed text-stone-400 transition-colors duration-500 group-hover:text-stone-300">
                      {a.copy}
                    </span>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
